/**
 * @fileoverview ChatGPT Notification Builders
 *
 * ChatGPT'ye gonderilecek ozet ve uyari mesajlarini olusturan fonksiyonlar.
 * useListingDetail ve useMapSearch hook'larindan extract edildi.
 *
 * @module utils/chatgpt-notifier
 */

import {
  getQuickInfo,
  parseBuildingAge,
  isNewListing,
  getAttributesByCategory,
  getPropertyValue,
} from "./listing-helpers";
import {
  getCheapestListings,
  calculateAreaAverage,
  calculateRoomDistribution,
} from "./search-helpers";
import { calculatePriceStats, isSendMessageAvailable } from "./index";

/**
 * ChatGPT icin ilan ozetini olusturur.
 *
 * Ilan detaylarindan ChatGPT'nin anlayabilecegi yapisal bir ozet cikarir.
 *
 * @param {Object} listing - Ilan detay nesnesi
 * @returns {Object} ChatGPT icin formatlanmis ozet nesnesi
 */
export function buildListingSummary(listing) {
  const quickInfos = listing.quickInfos || [];
  const attributes = listing.attributes || [];
  const properties = listing.properties || [];
  const buildingAgeStr = getQuickInfo(quickInfos, "building-age");

  // Site ozellikleri (havuz, guvenlik, otopark vb.)
  const siteOzellikleri = getAttributesByCategory(attributes, "site");
  const icOzellikler = getAttributesByCategory(attributes, "iç");
  const disOzellikler = getAttributesByCategory(attributes, "dış");

  return {
    baslik: listing.title,
    konum: listing.location,
    url: `https://www.emlakjet.com/ilan/${listing.id}`,
    fiyat: {
      toplam: listing.price,
      m2: listing.squareMeterPrice,
      paraBirimi: listing.currency || "TRY",
      degisimOrani: listing.priceChangeRate,
    },
    temelOzellikler: {
      alan: listing.m2,
      oda: listing.room,
      kat: listing.floor,
      binaYasi: buildingAgeStr,
      isitma: getQuickInfo(quickInfos, "heating"),
      aidat: getQuickInfo(quickInfos, "dues"),
      banyo: getQuickInfo(quickInfos, "bathroom-count"),
      balkon: getQuickInfo(quickInfos, "balcony"),
    },
    detayliOzellikler: {
      yapiTipi:
        getQuickInfo(quickInfos, "building-type") ||
        getPropertyValue(properties, "building_type"),
      cephe:
        getQuickInfo(quickInfos, "front") ||
        getPropertyValue(properties, "front"),
      esyaDurumu:
        getQuickInfo(quickInfos, "furnished") ||
        getPropertyValue(properties, "furnished"),
      tapuDurumu:
        getQuickInfo(quickInfos, "deed-status") ||
        getPropertyValue(properties, "deed_status"),
      kullanimDurumu:
        getQuickInfo(quickInfos, "usage-status") ||
        getPropertyValue(properties, "usage_status"),
      krediyeUygun:
        getQuickInfo(quickInfos, "credit-eligible") ||
        getPropertyValue(properties, "credit_eligible"),
    },
    siteOzellikleri: siteOzellikleri.length > 0 ? siteOzellikleri : null,
    icOzellikler: icOzellikler.length > 0 ? icOzellikler.slice(0, 10) : null,
    disOzellikler: disOzellikler.length > 0 ? disOzellikler.slice(0, 10) : null,
    ilanTipi: {
      islem: listing.tradeType,
      emlakTipi: listing.estateType,
    },
    sahibinden: listing.fromOwner,
    ilanTarihi: listing.createdAt,
    aciklama: listing.description?.slice(0, 300) || null,
  };
}

/**
 * Ilan verilerine dayali proaktif uyarilar olusturur.
 *
 * Kullaniciya faydali olabilecek uyarilari tespit eder:
 * - Fiyat dususu (yuzde olarak)
 * - Yeni ilan (son 3 gun icinde)
 * - Deprem riski (1999 oncesi binalar, 25+ yas)
 *
 * @param {Object} listing - Ilan detay nesnesi
 * @returns {string[]} Uyari mesajlari dizisi
 */
export function buildAlerts(listing) {
  const alerts = [];
  const quickInfos = listing.quickInfos || [];
  const buildingAgeStr = getQuickInfo(quickInfos, "building-age");
  const buildingAge = parseBuildingAge(buildingAgeStr);

  // Fiyat dusmus
  if (listing.priceChangeRate && listing.priceChangeRate < 0) {
    const pct = Math.abs(listing.priceChangeRate).toFixed(1);
    alerts.push(`Fiyat %${pct} dusmus`);
  }

  // Yeni ilan
  if (isNewListing(listing.createdAt, 3)) {
    alerts.push("Yeni ilan (son 3 gun)");
  }

  // Deprem riski (1999 oncesi = 25+ yas)
  if (buildingAge && buildingAge > 25) {
    alerts.push(`Bina ${buildingAge}+ yasinda, deprem guclendirme sor`);
  }

  return alerts;
}

/**
 * ChatGPT'ye ilan baglami gonderir.
 *
 * @param {Object} listing - Ilan detay nesnesi
 * @returns {void}
 */
// Debug log buffer - UI'da göstermek için
let _debugLogs = [];
let _debugListeners = [];

export function getDebugLogs() { return _debugLogs; }
export function clearDebugLogs() { _debugLogs = []; }
export function subscribeDebugLogs(listener) {
  _debugListeners.push(listener);
  return () => {
    _debugListeners = _debugListeners.filter(l => l !== listener);
  };
}

export function debugLog(msg) {
  // Disabled - uncomment below to re-enable debug logging
  return;
  /*
  const now = new Date();
  const ts = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  _debugLogs.push(`[${ts}] ${msg}`);
  if (_debugLogs.length > 10) _debugLogs.shift();
  // Notify listeners async (avoid setState during render)
  queueMicrotask(() => {
    _debugListeners.forEach(l => l([..._debugLogs]));
  });
  */
}

export async function notifyListingDetail(listing) {
  debugLog(`notifyDetail: ${listing?.id}`);

  // Retry mechanism for first call timing issues
  const maxRetries = 3;
  const retryDelay = 200; // ms

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    // Her çağrıda window.openai'yi taze oku (mobil timing sorunları için)
    const api = window.openai;
    if (api && typeof api.sendFollowUpMessage === "function") {
      const summary = buildListingSummary(listing);
      const alerts = buildAlerts(listing);

      // İlan başlık ve konumunu açıkça belirt
      const title = listing.title || "İsimsiz ilan";
      const location = listing.location || "";
      const price = listing.price || "";

      let prompt = `"${title}" ilanına bakıyorum${location ? ` (${location})` : ""}${price ? ` - ${price}` : ""}. Bu ilan hakkında kısa ve samimi yorumla: ${JSON.stringify(summary)}`;

      if (alerts.length > 0) {
        prompt += ` [DİKKAT: ${alerts.join(", ")}]`;
      }

      debugLog(`calling sendFollowUpMessage (attempt ${attempt})...`);
      try {
        await api.sendFollowUpMessage({ prompt });
        debugLog("sendMsg OK");
        return; // Success - exit
      } catch (err) {
        debugLog(`sendMsg ERR: ${err?.message || err}`);
        return; // API exists but failed - don't retry
      }
    }

    // API not ready - wait and retry
    if (attempt < maxRetries) {
      debugLog(`sendMsg NOT available (attempt ${attempt}), retrying in ${retryDelay}ms...`);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  debugLog("sendMsg NOT available after all retries");
}

/**
 * Harita arama sonuclari icin ozet olusturur.
 *
 * @param {Object} structured - Yapilandirilmis arama sonucu
 * @param {Object} bounds - Harita sinirlari
 * @returns {Object} ChatGPT icin formatlanmis ozet
 */
export function buildMapSearchSummary(structured, bounds) {
  const total = structured.total || 0;
  const listings = structured.listings || [];

  const priceStats = calculatePriceStats(listings);
  const cheapestListings = getCheapestListings(listings, 3);
  const areaAverage = calculateAreaAverage(listings);
  const roomDistribution = calculateRoomDistribution(listings);

  return {
    bolge: {
      koordinatlar: {
        guneybati: bounds.bottom_left,
        kuzeydogu: bounds.top_right,
      },
    },
    sonuc: {
      toplamIlan: total,
      gosterilenIlan: listings.length,
    },
    fiyatIstatistik: priceStats,
    alanOrtalama: areaAverage ? `${areaAverage} m²` : null,
    odaDagilimi: roomDistribution,
    enUcuz3Ilan: cheapestListings,
  };
}

/**
 * ChatGPT'ye harita arama sonuclarini bildirir.
 *
 * @param {Object} structured - Yapilandirilmis arama sonucu
 * @param {Object} bounds - Harita sinirlari
 * @returns {void}
 */
export async function notifyMapSearchResults(structured, bounds) {
  debugLog(`notifyMap: ${structured?.total} results`);

  // Retry mechanism for timing issues
  const maxRetries = 3;
  const retryDelay = 200; // ms

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const api = window.openai;
    if (api && typeof api.sendFollowUpMessage === "function") {
      const summary = buildMapSearchSummary(structured, bounds);
      const total = structured.total || 0;
      const listings = structured.listings || [];

      // En ucuz ilanların başlıklarını al (varsa)
      const topListings = listings
        .slice(0, 3)
        .map((l) => l.title || l.location)
        .filter(Boolean)
        .join(", ");

      let prompt;
      if (total === 0) {
        prompt = `Haritada seçilen bölgede ilan bulunamadı. Başka bir bölgeye bakmalı mıyız? ${JSON.stringify(summary)}`;
      } else {
        const locationHint = topListings ? ` Öne çıkan ilanlar: ${topListings}.` : "";
        prompt = `Haritada seçilen bölgede ${total} ilan buldum.${locationHint} Sonuçları özetle: ${JSON.stringify(summary)}`;
      }

      debugLog(`calling sendFollowUpMessage (map, attempt ${attempt})...`);
      try {
        await api.sendFollowUpMessage({ prompt });
        debugLog("sendMsg OK (map)");
        return;
      } catch (err) {
        debugLog(`sendMsg ERR (map): ${err?.message || err}`);
        return;
      }
    }

    if (attempt < maxRetries) {
      debugLog(`sendMsg NOT available (map, attempt ${attempt}), retrying...`);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  debugLog("sendMsg NOT available (map) after all retries");
}

// ============================================
// LAND (ARSA) SPECIFIC NOTIFICATIONS
// ============================================

/**
 * Arsa ilani icin ChatGPT ozeti olusturur.
 *
 * Arsa ozel bilgiler:
 * - Tapu durumu (mustakil, hisseli)
 * - Imar durumu (konut, ticari, villa imarli)
 * - Gabari ve KAKS degerleri
 * - Altyapi ozellikleri (elektrik, su, yol, vb.)
 * - Kat karsiligi uygunlugu
 *
 * @param {Object} listing - Arsa ilan detay nesnesi
 * @returns {Object} ChatGPT icin formatlanmis arsa ozeti
 */
export function buildLandListingSummary(listing) {
  const properties = listing.properties || [];
  const attributes = listing.attributes || {};

  // Arsa ozel property'leri al
  const tapuDurumu = getPropertyValue(properties, "deed_status");
  const imarDurumu = getPropertyValue(properties, "estate_type");
  const gabari = getPropertyValue(properties, "gabari_of_listing");
  const kaks = getPropertyValue(properties, "kaks_of_listing");
  const katKarsiligi = getPropertyValue(properties, "flat_for_construction");
  const ipotekDurumu = getPropertyValue(properties, "mortgage_status");
  const krediyeUygun = getPropertyValue(properties, "suitability_for_credit");
  const yatirimUygun = getPropertyValue(properties, "suitability_for_investor");
  const ada = getPropertyValue(properties, "block_of_buildings");
  const parsel = getPropertyValue(properties, "parcel_of_land");
  const pafta = getPropertyValue(properties, "sheet");
  const takas = getPropertyValue(properties, "trade");

  // Altyapi ozellikleri (Dis Ozellikler altinda)
  const disOzellikler = attributes["Dış Özellikler"] || {};
  const altyapiOzellikleri = disOzellikler["Altyapı Özellikleri"] || [];

  return {
    baslik: listing.title,
    konum: listing.location,
    url: `https://www.emlakjet.com/ilan/${listing.id}`,
    fiyat: {
      toplam: listing.price,
      m2Fiyati: listing.squareMeterPrice,
      paraBirimi: listing.currency || "TRY",
      degisimOrani: listing.priceChangeRate,
    },
    arsaBilgileri: {
      alan: listing.m2 ? `${listing.m2} m²` : null,
      imarDurumu: imarDurumu,
      tapuDurumu: tapuDurumu,
      gabari: gabari,
      kaks: kaks,
      katKarsiligi: katKarsiligi,
      ipotekDurumu: ipotekDurumu,
    },
    tapiKayitlari: {
      ada: ada,
      parsel: parsel,
      pafta: pafta,
    },
    altyapiOzellikleri: altyapiOzellikleri.length > 0 ? altyapiOzellikleri : null,
    degerBilgileri: {
      krediyeUygun: krediyeUygun,
      yatirimUygun: yatirimUygun,
      takas: takas,
    },
    ilanTarihi: listing.createdAt,
    aciklama: listing.description?.slice(0, 300) || null,
  };
}

/**
 * Arsa ilani icin proaktif uyarilar olusturur.
 *
 * @param {Object} listing - Arsa ilan detay nesnesi
 * @returns {string[]} Uyari mesajlari dizisi
 */
export function buildLandAlerts(listing) {
  const alerts = [];
  const properties = listing.properties || [];
  const attributes = listing.attributes || {};

  // Fiyat dusmus
  if (listing.priceChangeRate && listing.priceChangeRate < 0) {
    const pct = Math.abs(listing.priceChangeRate).toFixed(1);
    alerts.push(`Fiyat %${pct} dusmus`);
  }

  // Yeni ilan
  if (isNewListing(listing.createdAt, 3)) {
    alerts.push("Yeni ilan (son 3 gun)");
  }

  // Kat karsiligi verilebilir
  const katKarsiligi = getPropertyValue(properties, "flat_for_construction");
  if (katKarsiligi && katKarsiligi.toLowerCase().includes("verilebilir")) {
    alerts.push("Kat karsiligi verilebilir");
  }

  // Altyapi ozellikleri kontrol
  const disOzellikler = attributes["Dış Özellikler"] || {};
  const altyapi = disOzellikler["Altyapı Özellikleri"] || [];

  if (altyapi.includes("Parselli")) {
    alerts.push("Parselli");
  }
  if (altyapi.includes("İfrazlı")) {
    alerts.push("Ifrazli");
  }
  if (altyapi.includes("Projeli Ruhsatlı")) {
    alerts.push("Projeli & ruhsatli");
  }

  // Ipotek uyarisi
  const ipotekDurumu = getPropertyValue(properties, "mortgage_status");
  if (ipotekDurumu && ipotekDurumu.toLowerCase().includes("var")) {
    alerts.push("Ipotek var - dikkat");
  }

  // Hisseli tapu uyarisi
  const tapuDurumu = getPropertyValue(properties, "deed_status");
  if (tapuDurumu && tapuDurumu.toLowerCase().includes("hisseli")) {
    alerts.push("Hisseli tapu - diger hissedarlari sor");
  }

  // Krediye uygun
  const krediyeUygun = getPropertyValue(properties, "suitability_for_credit");
  if (krediyeUygun && krediyeUygun.toLowerCase().includes("uygun")) {
    alerts.push("Krediye uygun");
  }

  return alerts;
}

/**
 * ChatGPT'ye arsa ilan detayini bildirir.
 *
 * @param {Object} listing - Arsa ilan detay nesnesi
 * @returns {void}
 */
export async function notifyLandListingDetail(listing) {
  debugLog(`notifyLandDetail: ${listing?.id}`);

  const maxRetries = 3;
  const retryDelay = 200;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const api = window.openai;
    if (api && typeof api.sendFollowUpMessage === "function") {
      const summary = buildLandListingSummary(listing);
      const alerts = buildLandAlerts(listing);

      const title = listing.title || "Isimsiz arsa ilani";
      const location = listing.location || "";
      const price = listing.price || "";
      const area = listing.m2 ? `${listing.m2} m²` : "";

      let prompt = `"${title}" arsa ilanina bakiyorum${location ? ` (${location})` : ""}${price ? ` - ${price}` : ""}${area ? ` - ${area}` : ""}. Bu arsa hakkinda kisa ve samimi yorumla, yatirim potansiyelini degerlendir: ${JSON.stringify(summary)}`;

      if (alerts.length > 0) {
        prompt += ` [DIKKAT: ${alerts.join(", ")}]`;
      }

      debugLog(`calling sendFollowUpMessage (land, attempt ${attempt})...`);
      try {
        await api.sendFollowUpMessage({ prompt });
        debugLog("sendMsg OK (land)");
        return;
      } catch (err) {
        debugLog(`sendMsg ERR (land): ${err?.message || err}`);
        return;
      }
    }

    if (attempt < maxRetries) {
      debugLog(`sendMsg NOT available (land, attempt ${attempt}), retrying...`);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  debugLog("sendMsg NOT available (land) after all retries");
}

/**
 * Bir ilanin arsa ilani olup olmadigini kontrol eder.
 *
 * @param {Object} listing - Ilan nesnesi
 * @returns {boolean} Arsa ilani ise true
 */
export function isLandListing(listing) {
  if (!listing) return false;

  const estateType = listing.estateType?.toLowerCase() || "";
  const category = listing.category?.toLowerCase() || "";

  const landKeywords = [
    "arsa", "tarla", "arazi", "zeytinlik", "bag", "bahce",
    "land", "plot", "field", "agricultural"
  ];

  return landKeywords.some(keyword =>
    estateType.includes(keyword) || category.includes(keyword)
  );
}

/**
 * Ilan tipine gore uygun notify fonksiyonunu cagirir.
 * Arsa ilanlari icin notifyLandListingDetail, diger ilanlar icin notifyListingDetail kullanir.
 *
 * @param {Object} listing - Ilan detay nesnesi
 * @returns {void}
 */
export async function notifyListingDetailSmart(listing) {
  if (isLandListing(listing)) {
    return notifyLandListingDetail(listing);
  }
  return notifyListingDetail(listing);
}
